import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { RootState, AppDispatch } from '../store';
import {
  setTraceColor,
  setTraceWidth,
  setTraceOpacity,
  setTraceLineStyle,
  setTraceLineCap,
  setTraceLineJoin,
  TraceState
} from '../store/traceSlice';
import { Field, Input, Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { HiShoppingCart } from 'react-icons/hi';
import { useAddToCart } from '../utils/useAddToCart';

// Types explicites pour les options
interface LineStyleOptionType { value: string; labelKey: string; Preview: () => JSX.Element; }
interface LineCapOptionType { value: string; labelKey: string; Preview: () => JSX.Element; }
interface LineJoinOptionType { value: string; labelKey: string; Preview: () => JSX.Element; }

const LINE_STYLE_OPTIONS: LineStyleOptionType[] = [
  { value: 'solid', labelKey: 'tracePage.lineStyleSolid', Preview: () => <LineStylePreview style="solid" /> },
  { value: 'dashed', labelKey: 'tracePage.lineStyleDashed', Preview: () => <LineStylePreview style="dashed" /> },
  { value: 'dotted', labelKey: 'tracePage.lineStyleDotted', Preview: () => <LineStylePreview style="dotted" /> },
];
const LINE_CAP_OPTIONS: LineCapOptionType[] = [
  { value: 'butt', labelKey: 'tracePage.lineCapButt', Preview: () => <LineCapPreview cap="butt" /> },
  { value: 'round', labelKey: 'tracePage.lineCapRound', Preview: () => <LineCapPreview cap="round" /> },
  { value: 'square', labelKey: 'tracePage.lineCapSquare', Preview: () => <LineCapPreview cap="square" /> },
];
const LINE_JOIN_OPTIONS: LineJoinOptionType[] = [
  { value: 'bevel', labelKey: 'tracePage.lineJoinBevel', Preview: () => <LineJoinPreview join="bevel" /> },
  { value: 'round', labelKey: 'tracePage.lineJoinRound', Preview: () => <LineJoinPreview join="round" /> },
  { value: 'miter', labelKey: 'tracePage.lineJoinMiter', Preview: () => <LineJoinPreview join="miter" /> },
];

// --- SVGs pour les aperçus ---
const LineStylePreview: React.FC<{ style: LineStyleOption }> = ({ style }) => {
  const commonProps = { stroke: 'currentColor', strokeWidth: 2, fill: 'none' };
  let path;
  switch (style) {
    case 'dashed':
      path = <line x1="2" y1="10" x2="28" y2="10" {...commonProps} strokeDasharray="4 4" />;
      break;
    case 'dotted':
      path = <line x1="2" y1="10" x2="28" y2="10" {...commonProps} strokeDasharray="1 4" strokeLinecap="round" />;
      break;
    case 'solid':
    default:
      path = <line x1="2" y1="10" x2="28" y2="10" {...commonProps} />;
      break;
  }
  return <svg width="30" height="20" viewBox="0 0 30 20" className="mr-2 flex-shrink-0">{path}</svg>;
};

const LineCapPreview: React.FC<{ cap: LineCapOption }> = ({ cap }) => (
  <svg width="30" height="20" viewBox="0 0 30 20" className="mr-2 flex-shrink-0">
    <line x1="5" y1="10" x2="25" y2="10" stroke="currentColor" strokeWidth="4" strokeLinecap={cap} />
  </svg>
);

const LineJoinPreview: React.FC<{ join: LineJoinOption }> = ({ join }) => (
  <svg width="30" height="20" viewBox="0 0 30 20" className="mr-2 flex-shrink-0">
    <polyline points="5,15 15,5 25,15" stroke="currentColor" strokeWidth="3" fill="none" strokeLinejoin={join} />
  </svg>
);
// --- Fin SVGs ---

interface TraceProps {
  editorPreviewRef: React.RefObject<any>;
}

const Trace: React.FC<TraceProps> = ({ editorPreviewRef }) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { color, width, opacity, lineStyle, lineCap, lineJoin } = useSelector((state: RootState) => state.trace);

  const { handleAddToCart, isAddingToCart, cartOverlay } = useAddToCart(editorPreviewRef);

  const [localColor, setLocalColor] = useState<string>(color);
  const [localWidth, setLocalWidth] = useState<number>(width);
  const [localOpacity, setLocalOpacity] = useState<number>(opacity * 100);

  // Debounced actions
  const debouncedSetTraceColor = useCallback(
    debounce((color: string) => dispatch(setTraceColor(color)), 200),
    [dispatch]
  );
  const debouncedSetTraceWidth = useCallback(
    debounce((width: number) => dispatch(setTraceWidth(width)), 200),
    [dispatch]
  );
  const debouncedSetTraceOpacity = useCallback(
    debounce((opacity: number) => dispatch(setTraceOpacity(opacity / 100)), 200), // Convert back to 0-1 range
    [dispatch]
  );

  // Update local state when Redux state changes
  useEffect(() => {
    setLocalColor(color);
  }, [color]);

  useEffect(() => {
    setLocalWidth(width);
  }, [width]);

  useEffect(() => {
    setLocalOpacity(opacity * 100); // Convert to 0-100 range for slider
  }, [opacity]);

  // Handlers that update local state and call debounced actions
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalColor(e.target.value);
    debouncedSetTraceColor(e.target.value);
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalWidth(value);
    debouncedSetTraceWidth(value);
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalOpacity(value);
    debouncedSetTraceOpacity(value);
  };

  const handleLineStyleChange = (value: string) => {
    dispatch(setTraceLineStyle(value));
  };

  const handleLineCapChange = (value: string) => {
    dispatch(setTraceLineCap(value));
  };

  const handleLineJoinChange = (value: string) => {
    dispatch(setTraceLineJoin(value));
  };

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6">
      {cartOverlay}
      <div>
        <h1 className="text-xl font-semibold text-white break-words whitespace-normal">{t('pageTitles.trace')}</h1>
        <p className="text-sm text-gray-400 mt-1 break-words whitespace-normal">
          {t('tracePage.subtitle')}
        </p>
      </div>

      {/* Color Picker */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('tracePage.color')}</Label>
        <div className="mt-1 flex items-center">
          <input
            type="color"
            value={localColor}
            onChange={handleColorChange}
            className="w-8 h-8 rounded-md border-gray-600 cursor-pointer focus:ring-blue-500 focus:border-blue-500 bg-transparent mr-2"
          />
          <Input
            type="text"
            value={localColor}
            onChange={handleColorChange}
            className="block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
          />
        </div>
      </Field>

      {/* Width Slider */}
      <Field>
        <Label className="text-sm font-medium text-white">
          {t('tracePage.thickness')}
        </Label>
        <Input
          type="number"
          min="1"
          max="20"
          value={localWidth}
          onChange={handleWidthChange}
          className="mt-1 block w-full rounded-lg border-none bg-white/5 py-1.5 px-3 text-sm/6 focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
        />
        <Input
          type="range"
          min="1"
          max="20"
          value={localWidth}
          onChange={handleWidthChange}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
        />
      </Field>

      {/* Opacity Slider */}
      <Field>
        <Label className="text-sm font-medium text-white">
          {t('tracePage.opacity')}
        </Label>
        <Input
          type="number"
          min="0"
          max="100"
          value={localOpacity}
          onChange={handleOpacityChange}
          className="mt-1 block w-full rounded-lg border-none bg-white/5 py-1.5 px-3 text-sm/6 focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
        />
        <Input
          type="range"
          min="0"
          max="100"
          value={localOpacity}
          onChange={handleOpacityChange}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
        />
      </Field>

      {/* Line Style Select */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('tracePage.lineStyle')}</Label>
        <Listbox value={lineStyle} onChange={handleLineStyleChange}>
          <ListboxButton
            className={clsx(
              "relative mt-1 block w-full rounded-lg bg-white/5 py-1.5 pr-8 pl-3 text-left text-sm/6",
              "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            )}
          >
            <span className="flex items-center">
              {LINE_STYLE_OPTIONS.find(o => o.value === lineStyle)?.Preview()}
              <span className="ml-2">{t(LINE_STYLE_OPTIONS.find(o => o.value === lineStyle)?.labelKey || '')}</span>
            </span>
            <ChevronDownIcon
              className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
              aria-hidden="true"
            />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              "w-[var(--button-width)] z-10 mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
              "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
            )}
          >
            {LINE_STYLE_OPTIONS.map(option => (
              <ListboxOption key={option.value} value={option.value} className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10">
                <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible flex-shrink-0" />
                {option.Preview()}
                <div className="text-sm/6">
                  {t(option.labelKey)}
                </div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      </Field>

      {/* Line Cap Select */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('tracePage.lineCap')}</Label>
        <Listbox value={lineCap} onChange={handleLineCapChange}>
          <ListboxButton
            className={clsx(
              "relative mt-1 block w-full rounded-lg bg-white/5 py-1.5 pr-8 pl-3 text-left text-sm/6",
              "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            )}
          >
            <span className="flex items-center">
              {LINE_CAP_OPTIONS.find(o => o.value === lineCap)?.Preview()}
              <span className="ml-2">{t(LINE_CAP_OPTIONS.find(o => o.value === lineCap)?.labelKey || '')}</span>
            </span>
            <ChevronDownIcon
              className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
              aria-hidden="true"
            />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              "w-[var(--button-width)] z-10 mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
              "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
            )}
          >
            {LINE_CAP_OPTIONS.map(option => (
              <ListboxOption key={option.value} value={option.value} className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10">
                <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible flex-shrink-0" />
                {option.Preview()}
                <div className="text-sm/6">
                  {t(option.labelKey)}
                </div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      </Field>

      {/* Line Join Select */}
      <Field>
        <Label className="text-sm font-medium text-white">{t('tracePage.lineJoin')}</Label>
        <Listbox value={lineJoin} onChange={handleLineJoinChange}>
          <ListboxButton
            className={clsx(
              "relative mt-1 block w-full rounded-lg bg-white/5 py-1.5 pr-8 pl-3 text-left text-sm/6",
              "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            )}
          >
            <span className="flex items-center">
              {LINE_JOIN_OPTIONS.find(o => o.value === lineJoin)?.Preview()}
              <span className="ml-2">{t(LINE_JOIN_OPTIONS.find(o => o.value === lineJoin)?.labelKey || '')}</span>
            </span>
            <ChevronDownIcon
              className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
              aria-hidden="true"
            />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              "w-[var(--button-width)] z-10 mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
              "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
            )}
          >
            {LINE_JOIN_OPTIONS.map(option => (
              <ListboxOption key={option.value} value={option.value} className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10">
                <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible flex-shrink-0" />
                {option.Preview()}
                <div className="text-sm/6">
                  {t(option.labelKey)}
                </div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      </Field>

      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart}
        className="w-full mt-6 flex justify-center items-center space-x-2 text-white py-2 rounded-sm text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-wait"
        type="button"
        style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
      >
        <HiShoppingCart className="w-4 h-4" />
        <span>{t('common.addToCart', 'Ajouter au panier')}</span>
      </button>
    </div>
  );
};

export default Trace;